import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import {
  Container,
  Content,
  Loader,
  Message,
  Button,
  ButtonGroup,
  Modal,
  useToaster,
  Panel,
} from 'rsuite';
import { ArrowDown } from '@rsuite/icons';

import NavigationBar from '../Shared/NavigationBar';
import EditEntryModal from './EditEntryModal';
import AddToCollectionModal from './AddToCollectionModal';
import AddCardSidePanelModal from './AddCardSidePanelModal';
import CollectionFilters from './CollectionFilters';
import ExportModal from './ExportModal';
import ImportDeckModal from './ImportDeckModal';
import ShareCollectionModal from './ShareCollectionModal';
import CardQuickViewModal from './CardQuickViewModal';
import DeckInsights from './DeckInsights';
import DeckControlsBar from './DeckControlsBar';
import EditDeckModal from './EditDeckModal';
import ErrorBoundary from './ErrorBoundary';
import DeckHeader from './DeckHeader';
import DeckSummary from './DeckSummary';
import DeckContent from './DeckContent';
import BulkDeleteModal from './Modals/BulkDeleteModal';
import DeleteEntryModal from './Modals/DeleteEntryModal';
import MarkOwnershipModal from './Modals/MarkOwnershipModal';
import ConvertProxiesModal from './Modals/ConvertProxiesModal';
import { sortAndGroupCards } from './deckSortingUtils';
import useDeckPreferences from './useDeckPreferences';
import authFetch, { API_BASE } from '../../helpers/authFetch';
import { DECK_FORMAT_RULES } from '../../helpers/deckRules';
import { parseDropData, resolveCardFromDrop } from '../../helpers/dragDropCardParser';
import { RARITY_ORDER, CONDITION_ORDER, FINISH_ORDER } from './CardAttributeDefaults';
import './CollectionDetail.css';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';
import { VIEW_MODES } from '../../helpers/viewModeConstants';


const SPACING = {
  containerMaxWidth: 1100,
  containerPadding: 16,
  containerMarginTop: 30,
  headerMarginBottom: 20,
  emptyPadding: 60,
  statsGap: 16,
  controlsGap: 16,
  controlsMarginBottom: 16,
  actionItemGap: 12,
};

const buildColors = (theme) => {
  const t = getThemeColors(theme);
  return {
    muted: t.textMuted,
    gain: '#2ecc71',
    loss: '#e74c3c',
    dropHighlight: t.accentSecondary,
    dropHighlightBg: theme === 'light' ? 'rgba(25, 113, 194, 0.1)' : 'rgba(52, 152, 219, 0.1)',
    hintText: t.textFaint,
  };
};

const TOAST_DURATION = 5000;
const BATCH_MAX_ATTEMPTS = 4;
const BATCH_BASE_DELAY_MS = 1000;


const defaultColumns = [
  { key: 'name', label: 'Name', fixed: true, flexGrow: 2, sortable: true },
  { key: 'type_line', label: 'Type', flexGrow: 1, sortable: true },
];

const desktopColumns = [
  { key: 'rarity', label: 'Rarity', flexGrow: 1, sortable: true },
  { key: 'colors', label: 'Colors', flexGrow: 1 },
  { key: 'set_name', label: 'Set', flexGrow: 1 },
  { key: 'finish', label: 'Finish', flexGrow: 0.8, sortable: true },
  { key: 'purchase_price_display', label: 'Purchase Price', flexGrow: 1, sortable: true, sortKey: 'purchase_price_raw' },
  { key: 'current_price', label: 'Current Price', flexGrow: 1, sortable: true, sortKey: 'current_price_raw' },
  { key: 'gain_loss', label: 'Gain/Loss', flexGrow: 1, custom: true },
  { key: 'condition', label: 'Condition', flexGrow: 1, sortable: true },
];

const quantityColumn = { key: 'quantity', label: 'Quantity', width: 90, sortable: true };

const CollectionDetail = ({ readOnly = false, shareSlug = null }) => {
  const { preferences, updatePreference } = usePreferences();
  const COLORS = buildColors(preferences.theme);
  const { id: routeId } = useParams();
  const id = shareSlug ? null : routeId;
  const canEdit = !readOnly;
  const navigate = useNavigate();
  const [collection, setCollection] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardLoading, setCardLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [nameSearch, setNameSearch] = useState('');
  const [colorFilter, setColorFilter] = useState([]);
  const [rarityFilter, setRarityFilter] = useState([]);
  const [conditionFilter, setConditionFilter] = useState([]);
  const [notOwnedFilter, setNotOwnedFilter] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exportOpen, setExportOpen] = useState(false);
  const [importDeckModalOpen, setImportDeckModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [editDeckModalOpen, setEditDeckModalOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [paginationProgress, setPaginationProgress] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [deckSection, setDeckSection] = useState('all');
  const [viewMode, setViewMode] = useState(preferences.card_display_mode);
  const [deckPrefs, updateDeckPref] = useDeckPreferences(collection?.id, viewMode);
  const [bulkMarkOwnershipModal, setBulkMarkOwnershipModal] = useState(null);
  const [bulkMarkLoading, setBulkMarkLoading] = useState(false);
  const [skipProxies, setSkipProxies] = useState(true);
  const [convertProxiesModalOpen, setConvertProxiesModalOpen] = useState(false);
  const [convertProxiesLoading, setConvertProxiesLoading] = useState(false);
  const [cardPopupOpen, setCardPopupOpen] = useState(false);
  const [cardPopupEntry, setCardPopupEntry] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addCardData, setAddCardData] = useState(null);
  const [addSidePanelModalOpen, setAddSidePanelModalOpen] = useState(false);
  const [dragOverActive, setDragOverActive] = useState(false);
  const [resolvingDrop, setResolvingDrop] = useState(false);
  const [deckCompletion, setDeckCompletion] = useState(null);
  const addedViaDropRef = useRef(false);
  const completionFetchedRef = useRef(false);
  const priceCacheRef = useRef({});
  const toaster = useToaster();
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    completionFetchedRef.current = false;
    setDeckCompletion(null);
  }, [collection?.id, refreshKey]);

  useEffect(() => {
    if (completionFetchedRef.current) return;
    if (tableData.length === 0 || cardLoading !== false || (collection?.type !== 'DECK')) return;
    const mainEntries = tableData.filter(e => !e.is_sideboard);
    if (mainEntries.length === 0) return;
    completionFetchedRef.current = true;
    const ownedEntries = mainEntries.filter(e => !e.is_not_owned);
    const cards = ownedEntries.map(e => ({ scryfall_id: e.scryfall_id, required_quantity: e.quantity }));
    if (ownedEntries.length === 0) {
      const totalCount = mainEntries.reduce((sum, e) => sum + (e.quantity || 0), 0);
      setDeckCompletion({ owned: 0, total: totalCount });
      return;
    }
    const fetchCompletion = async () => {
      try {
        const resp = await authFetch('/collections/completion', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cards }),
        });
        if (!resp.ok) {
          console.warn('Deck completion fetch failed:', resp.status);
          return;
        }
        const data = await resp.json();
        if (!isMountedRef.current) return;
        const owned = data.owned || {};
        const ownedCount = ownedEntries.reduce((sum, e) => {
          const have = owned[e.scryfall_id] ?? 0;
          return sum + Math.min(have, e.quantity);
        }, 0);
        const totalCount = mainEntries.reduce((sum, e) => sum + (e.quantity || 0), 0);
        setDeckCompletion({ owned: ownedCount, total: totalCount });
      } catch (err) {
        console.warn('Deck completion fetch error:', err.message);
      }
    };
    fetchCompletion();
  }, [tableData, cardLoading, collection?.id, collection?.type]);

  const formatRules = collection ? DECK_FORMAT_RULES[collection.deck_type] : null;
  const hasSideboard = collection?.type === 'DECK' && formatRules?.sideboardSize != null && formatRules?.sideboardSize > 0;

  const mainDeckCards = tableData.filter(e => !e.is_sideboard).reduce((sum, e) => sum + (e.quantity || 0), 0);
  const sideboardCards = tableData.filter(e => e.is_sideboard).reduce((sum, e) => sum + (e.quantity || 0), 0);

  const enrichEntries = (entries, cards) => entries.map((entry, idx) => {
    const card = cards[idx];
    const finish = entry.finish || 'nonfoil';
    
    // Select price based on finish
    let usdPrice;
    if (finish === 'foil') {
      usdPrice = card?.prices?.usd_foil || card?.prices?.usd || null;
    } else if (finish === 'etched') {
      usdPrice = card?.prices?.usd_etched || card?.prices?.usd || null;
    } else {
      usdPrice = card?.prices?.usd || null;
    }
    
    const currentRaw = usdPrice ? Number(usdPrice) : null;
    const purchaseRaw = entry.purchase_price != null ? Number(entry.purchase_price) : null;
    const gainLossRaw = (purchaseRaw != null && currentRaw != null)
      ? currentRaw - purchaseRaw
      : null;
    const rawColors = card?.colors || [];

    // Extract image URIs for visual view (CT-54)
    const isMissingImage = card?.image_status === 'missing';
    let imageSmall = null;
    let imageNormal = null;
    if (card && !isMissingImage) {
      if (card.image_uris) {
        imageSmall = card.image_uris.small || null;
        imageNormal = card.image_uris.normal || null;
      } else if (card.card_faces && card.card_faces[0]?.image_uris) {
        imageSmall = card.card_faces[0].image_uris.small || null;
        imageNormal = card.card_faces[0].image_uris.normal || null;
      }
    }

    // Format set display with collector number
    const setDisplay = card?.collector_number
      ? `${card.set_name || ''} (#${card.collector_number})`
      : card?.set_name || '';

    return {
       ...entry,
       name: card?.name || 'Unknown Card',
       type_line: card?.type_line || '',
       rarity: card?.rarity || '',
       colors_raw: rawColors,
       colors: rawColors.join(', ') || 'Colorless',
       set_name: setDisplay,
       set_code: card?.set || '',
       collector_number: card?.collector_number || '',
       games: card?.games || [],
       cmc: card?.cmc ?? 0,
       image_small: imageSmall,
       image_normal: imageNormal,
       image_uris: card?.image_uris || null,
       card_faces: card?.card_faces || null,
       purchase_price_display: purchaseRaw != null
         ? `$${purchaseRaw.toFixed(2)}`
         : '',
       current_price: currentRaw != null ? `$${currentRaw.toFixed(2)}` : '',
       current_price_raw: currentRaw,
       purchase_price_raw: purchaseRaw,
       gain_loss_raw: gainLossRaw,
     };
  });

  const fetchCardsBatch = useCallback(async (scryfallIds) => {
    const cache = priceCacheRef.current;
    const uncachedIds = scryfallIds.filter((sid) => !cache[sid]);

    if (uncachedIds.length > 0) {
      const chunks = [];
      for (let i = 0; i < uncachedIds.length; i += 75) {
        chunks.push(uncachedIds.slice(i, i + 75));
      }

      let rateLimited = false;
      for (const chunk of chunks) {
        const identifiers = chunk.map((sid) => ({ id: sid }));
        let attempt = 0;
        while (attempt < BATCH_MAX_ATTEMPTS) {
          try {
            const res = await fetch(`${API_BASE}/cards/collection`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ identifiers }),
            });
            if (res.status === 429 || res.status === 503) {
              attempt++;
              if (attempt < BATCH_MAX_ATTEMPTS) {
                const retryAfter = res.headers.get('Retry-After');
                const delay = retryAfter
                  ? parseInt(retryAfter, 10) * 1000
                  : BATCH_BASE_DELAY_MS * Math.pow(2, attempt - 1);
                await new Promise(r => setTimeout(r, delay));
                continue;
              }
              rateLimited = true;
              break;
            }
            if (res.ok) {
              const data = await res.json();
              if (data.data) {
                data.data.forEach((card) => {
                  cache[card.id] = card;
                });
              }
            }
            break;
          } catch (err) {
            break;
          }
        }
      }

      if (rateLimited) {
        toaster.push(
          <Message showIcon type="warning" closable>
            Some card prices could not be loaded due to rate limiting. Refresh to try again.
          </Message>,
          { placement: 'topCenter', duration: TOAST_DURATION }
        );
      }
    }

    return scryfallIds.map((sid) => cache[sid] || null);
  }, [toaster]);

  useEffect(() => {
    const fetchCollection = async () => {
      setLoading(true);
      setError('');
      setTableData([]);
      setPaginationProgress(0);
      setTotalPages(0);
      setCurrentPageNum(1);

      try {
        // Shared mode: single fetch, no pagination
        if (shareSlug) {
          const resp = await authFetch(`/shared/collections/${shareSlug}`);
          if (!isMountedRef.current) return;
          if (!resp.ok) {
            if (resp.status === 404) setError('Deck or binder not found');
            else if (resp.status === 403) setError('You do not have access to this deck or binder');
            else setError('Failed to load deck or binder');
            return;
          }
          const data = await resp.json();
          if (!isMountedRef.current) return;
          setCollection(data);
          const entries = data.entries || [];
          if (entries.length > 0) {
            setCardLoading(true);
            setPriceLoading(true);
            const basicData = entries.map((entry) => ({
              ...entry,
              name: 'Loading...',
              type_line: '',
              rarity: '',
              colors_raw: [],
              colors: 'Colorless',
              set_name: '',
              current_price: '',
              current_price_raw: null,
              purchase_price_raw: entry.purchase_price != null ? Number(entry.purchase_price) : null,
              purchase_price_display: '',
              gain_loss_raw: null,
            }));
            setTableData(basicData);
            if (isMountedRef.current) setLoading(false);
            const cards = await fetchCardsBatch(entries.map((e) => e.scryfall_id));
            if (!isMountedRef.current) return;
            setTableData(enrichEntries(entries, cards));
            setCardLoading(false);
            setPriceLoading(false);
          }
          return;
        }

        // Fetch first page
        const firstResponse = await authFetch(`/collections/${id}?limit=100&page=1`);
        if (!isMountedRef.current) return;
        if (!firstResponse.ok) {
          if (firstResponse.status === 404) {
            setError('Collection not found');
          } else if (firstResponse.status === 403) {
            setError('You do not have access to this collection');
          } else {
            setError('Failed to load collection');
          }
          return;
        }
        const firstData = await firstResponse.json();
        if (!isMountedRef.current) return;

        setCollection(firstData);
        const totalPagesCount = firstData.pagination?.totalPages || 1;
        setTotalPages(totalPagesCount);

        if (firstData.entries && firstData.entries.length > 0) {
          setCardLoading(true);
          setPriceLoading(true);

          // Show first page immediately without Scryfall enrichment
          const basicData = firstData.entries.map((entry) => ({
            ...entry,
            name: 'Unknown Card',
            type_line: '',
            rarity: '',
            colors_raw: [],
            colors: 'Colorless',
            set_name: '',
            purchase_price_display: entry.purchase_price != null
              ? `$${Number(entry.purchase_price).toFixed(2)}`
              : '',
            current_price: '',
            current_price_raw: null,
            purchase_price_raw: entry.purchase_price != null ? Number(entry.purchase_price) : null,
            gain_loss_raw: null,
          }));
          setTableData(basicData);
          // Hide loading spinner - table is now visible with basic data
          if (isMountedRef.current) setLoading(false);

          // Enrich first page in background
          const scryfallIds = firstData.entries.map((e) => e.scryfall_id);
          const cards = await fetchCardsBatch(scryfallIds);
          if (!isMountedRef.current) return;

          const enriched = enrichEntries(firstData.entries, cards);

          setTableData(enriched);
          if (isMountedRef.current) {
            setPaginationProgress(1 / totalPagesCount);
            setCurrentPageNum(1);
          }

          // Fetch remaining pages in background
          if (totalPagesCount > 1) {
            let allEnrichedData = enriched;
            const seenEntryIds = new Set(enriched.map((e) => e.id));

            for (let page = 2; page <= totalPagesCount; page++) {
              if (!isMountedRef.current) break;

              try {
                const pageResponse = await authFetch(`/collections/${id}?limit=100&page=${page}`);
                if (!pageResponse.ok) break;

                const pageData = await pageResponse.json();
                if (!isMountedRef.current) break;

                if (pageData.entries && pageData.entries.length > 0) {
                  // Enrich this page
                  const pageIds = pageData.entries.map((e) => e.scryfall_id);
                  const pageCards = await fetchCardsBatch(pageIds);
                  if (!isMountedRef.current) break;

                  const pageEnriched = enrichEntries(pageData.entries, pageCards);

                  // Deduplicate by entry id before appending
                  const newEntries = pageEnriched.filter((e) => {
                    if (seenEntryIds.has(e.id)) {
                      return false; // Skip duplicate
                    }
                    seenEntryIds.add(e.id);
                    return true;
                  });

                  // Append to table data
                  if (newEntries.length > 0) {
                    allEnrichedData = allEnrichedData.concat(newEntries);
                  }
                  if (isMountedRef.current) {
                    setTableData(allEnrichedData);
                    setPaginationProgress(page / totalPagesCount);
                    setCurrentPageNum(page);
                  }
                }
              } catch (err) {
                // Page fetch failed, continue with what we have
                console.error(`Failed to fetch page ${page}:`, err.message);
                if (isMountedRef.current) {
                  toaster.push(
                    <Message type="warning" showIcon closable>
                      Failed to load page {page}. Showing available data.
                    </Message>,
                    { placement: 'topCenter', duration: 3000 }
                  );
                }
              }
            }

            setCardLoading(false);
            setPriceLoading(false);
          } else {
            setCardLoading(false);
            setPriceLoading(false);
          }
        }
      } catch (err) {
        if (isMountedRef.current) setError('Unable to connect to server');
      } finally {
        // Ensure loading is hidden if error occurred before first page was displayed
        if (isMountedRef.current) setLoading(false);
      }
    };

    fetchCollection();
  }, [id, shareSlug, refreshKey, toaster, fetchCardsBatch]);

  const filteredData = useMemo(() => {
    let data = tableData;

    if (hasSideboard) {
      if (deckSection === 'main') {
        data = data.filter((row) => !row.is_sideboard);
      } else if (deckSection === 'side') {
        data = data.filter((row) => row.is_sideboard);
      }
    }

    if (nameSearch) {
      const term = nameSearch.toLowerCase();
      data = data.filter((row) => row.name.toLowerCase().includes(term));
    }

    if (colorFilter.length > 0) {
      data = data.filter((row) => {
        const raw = row.colors_raw || [];
        return colorFilter.some((f) => {
          if (f === 'C') return raw.length === 0;
          if (f === 'M') return raw.length > 1;
          return raw.includes(f);
        });
      });
    }

    if (rarityFilter.length > 0) {
      data = data.filter((row) => rarityFilter.includes(row.rarity));
    }

    if (conditionFilter.length > 0) {
      data = data.filter((row) => conditionFilter.includes(row.condition));
    }

    if (notOwnedFilter) {
      data = data.filter((row) => row.is_not_owned === true);
    }

    return data;
  }, [tableData, nameSearch, colorFilter, rarityFilter, conditionFilter, notOwnedFilter, deckSection, hasSideboard]);

  const stats = useMemo(() => {
    if (tableData.length === 0) {
      return {
        totalCardCount: 0,
        totalPurchaseValue: 0,
        colorBreakdown: {},
        rarityBreakdown: {},
        top10MostValuableCards: [],
      };
    }

    let totalCardCount = 0;
    let totalPurchaseValue = 0;
    const colorBreakdown = {};
    const rarityBreakdown = {};
    const cardsByPrice = [];

    tableData.forEach((row) => {
      const quantity = row.quantity || 1;

      // Total card count
      totalCardCount += quantity;

      // Total purchase value
      if (row.purchase_price_raw != null) {
        totalPurchaseValue += row.purchase_price_raw * quantity;
      }

      // Color breakdown
      const colors = row.colors_raw || [];
      let colorKey;
      if (colors.length === 0) {
        colorKey = 'Colorless';
      } else if (colors.length === 1) {
        colorKey = colors[0];
      } else {
        colorKey = 'Multicolor';
      }
      colorBreakdown[colorKey] = (colorBreakdown[colorKey] || 0) + quantity;

      // Rarity breakdown
      const rarity = row.rarity ? row.rarity.charAt(0).toUpperCase() + row.rarity.slice(1) : 'Unknown';
      rarityBreakdown[rarity] = (rarityBreakdown[rarity] || 0) + quantity;

      // Top 10 most valuable
      if (row.purchase_price_raw != null) {
        cardsByPrice.push({
          id: row.id,
          name: row.name,
          scryfall_id: row.scryfall_id,
          quantity: quantity,
          purchasePrice: row.purchase_price_raw,
          totalValue: row.purchase_price_raw * quantity,
        });
      }
    });

    // Get top 10 most valuable cards
    const top10MostValuableCards = cardsByPrice
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 10);

    return {
      totalCardCount,
      totalPurchaseValue: Math.round(totalPurchaseValue * 100) / 100,
      colorBreakdown,
      rarityBreakdown,
      top10MostValuableCards,
    };
  }, [tableData]);

  const allColumns = useMemo(() => {
    const hiddenColumnsInReadOnly = ['purchase_price_display', 'gain_loss'];
    const base = isMobile
      ? [...defaultColumns, quantityColumn]
      : [...defaultColumns, ...desktopColumns, quantityColumn];
    if (!canEdit) {
      return base.filter((col) => !hiddenColumnsInReadOnly.includes(col.key));
    }
    return base;
  }, [canEdit]);

  const handleSortColumn = (dataKey) => {
    const colDef = allColumns.find((c) => c.key === dataKey);
    const resolvedKey = colDef?.sortKey || dataKey;

    if (sortColumn === resolvedKey) {
      if (sortType === 'asc') {
        setSortType('desc');
      } else if (sortType === 'desc') {
        setSortColumn(null);
        setSortType(null);
      } else {
        setSortType('asc');
      }
    } else {
      setSortColumn(resolvedKey);
      setSortType('asc');
    }
  };

  const sortedData = useMemo(() => {
    let data = filteredData;
    
    if (sortColumn && sortType) {
      data = [...filteredData].sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];

        if (sortColumn === 'rarity') {
          const aRank = RARITY_ORDER[(aVal || '').toLowerCase()] ?? 99;
          const bRank = RARITY_ORDER[(bVal || '').toLowerCase()] ?? 99;
          return sortType === 'asc' ? aRank - bRank : bRank - aRank;
        }

        if (sortColumn === 'condition') {
          const aRank = CONDITION_ORDER[aVal] ?? 99;
          const bRank = CONDITION_ORDER[bVal] ?? 99;
          return sortType === 'asc' ? aRank - bRank : bRank - aRank;
        }

        if (sortColumn === 'finish') {
          const aRank = FINISH_ORDER[aVal || 'nonfoil'] ?? 99;
          const bRank = FINISH_ORDER[bVal || 'nonfoil'] ?? 99;
          return sortType === 'asc' ? aRank - bRank : bRank - aRank;
        }

        if (typeof aVal === 'number' || typeof bVal === 'number') {
          const an = aVal ?? -Infinity;
          const bn = bVal ?? -Infinity;
          return sortType === 'asc' ? an - bn : bn - an;
        }

        const as = (aVal || '').toString();
        const bs = (bVal || '').toString();
        return sortType === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
      });
    }
    
    // Pin commander card to top for COMMANDER deck type
    if (collection?.deck_type === 'COMMANDER') {
      const commander = data.find((row) => row.is_commander);
      if (commander) {
        data = [commander, ...data.filter((row) => !row.is_commander)];
      }
    }
    
    // Add unique _rowIndex to each row for stable selection
    return data.map((row, idx) => ({ ...row, _rowIndex: idx }));
  }, [filteredData, sortColumn, sortType, collection?.deck_type]);

  const groupedData = useMemo(() => {
    const grouped = sortAndGroupCards(sortedData, deckPrefs.sortBy);

    if (collection?.deck_type !== 'COMMANDER') {
      return grouped;
    }

    const commanderCards = [];
    const nonCommanderGroups = grouped
      .map((group) => {
        const filteredCards = group.cards.filter((card) => {
          if (card.is_commander) {
            commanderCards.push(card);
            return false;
          }
          return true;
        });
        return {
          ...group,
          cards: filteredCards,
          totalCount: filteredCards.reduce((sum, e) => sum + (e.quantity || 1), 0),
        };
      })
      .filter((group) => group.cards.length > 0);

    if (commanderCards.length > 0) {
      return [
        {
          sortKey: 'commander',
          label: 'Commander',
          cards: commanderCards,
          totalCount: commanderCards.reduce((sum, e) => sum + (e.quantity || 1), 0),
        },
        ...nonCommanderGroups,
      ];
    }

    return nonCommanderGroups;
  }, [sortedData, deckPrefs.sortBy, collection?.deck_type]);

  const tableGroupedData = useMemo(() => {
    // If table column sorting is active, use flat sorted data
    if (sortColumn) {
      return sortedData;
    }
    // Otherwise use grouped data
    const flatData = [];
    groupedData.forEach((group) => {
      flatData.push(...group.cards);
    });
    return flatData;
  }, [groupedData, sortColumn, sortedData]);

  const displaySortColumn = useMemo(() => {
    if (!sortColumn) return null;
    const col = allColumns.find((c) => (c.sortKey || c.key) === sortColumn);
    return col ? col.key : sortColumn;
  }, [sortColumn, allColumns]);

  // Build maps of scryfall_id -> error/warning info for highlighting
  const validationHighlights = useMemo(() => {
    const errorMap = {}; // scryfall_id -> { type, message }
    const warningMap = {}; // scryfall_id -> { type, message }

    if (!validationResult) {
      return { errorMap, warningMap };
    }

    if (validationResult.errors && Array.isArray(validationResult.errors)) {
      validationResult.errors.forEach((error) => {
        if (error.cards && Array.isArray(error.cards)) {
          error.cards.forEach((card) => {
            if (card.scryfall_id) {
              errorMap[card.scryfall_id] = {
                type: error.type,
                message: error.message,
              };
            }
          });
        }
      });
    }

    if (validationResult.warnings && Array.isArray(validationResult.warnings)) {
      validationResult.warnings.forEach((warning) => {
        if (warning.cards && Array.isArray(warning.cards)) {
          warning.cards.forEach((card) => {
            if (card.scryfall_id) {
              warningMap[card.scryfall_id] = {
                type: warning.type,
                message: warning.message,
              };
            }
          });
        }
      });
    }

    return { errorMap, warningMap };
  }, [validationResult]);

  const priceSummary = useMemo(() => {
    if (tableData.length === 0) {
      return {
        withPurchase: [],
        totalPurchase: 0,
        totalCurrent: 0,
        trackable: [],
        trackablePurchase: 0,
        trackableCurrent: 0,
        totalGainLoss: 0,
        glColor: COLORS.muted,
        glPrefix: '',
      };
    }

    const withPurchase = tableData.filter((r) => r.purchase_price_raw != null);
    const totalPurchase = withPurchase.reduce((s, r) => s + r.purchase_price_raw * (r.quantity || 1), 0);
    const totalCurrent = tableData.reduce((s, r) => s + (r.current_price_raw || 0) * (r.quantity || 1), 0);
    const trackable = withPurchase.filter((r) => r.current_price_raw != null);
    const trackablePurchase = trackable.reduce((s, r) => s + r.purchase_price_raw * (r.quantity || 1), 0);
    const trackableCurrent = trackable.reduce((s, r) => s + r.current_price_raw * (r.quantity || 1), 0);
    const totalGainLoss = trackableCurrent - trackablePurchase;
    const glColor = totalGainLoss > 0 ? COLORS.gain : totalGainLoss < 0 ? COLORS.loss : COLORS.muted;
    const glPrefix = totalGainLoss > 0 ? '+' : '';

    return {
      withPurchase,
      totalPurchase,
      totalCurrent,
      trackable,
      trackablePurchase,
      trackableCurrent,
      totalGainLoss,
      glColor,
      glPrefix,
    };
  }, [tableData, COLORS.gain, COLORS.loss, COLORS.muted]);

  const handleClearFilters = () => {
    setNameSearch('');
    setColorFilter([]);
    setRarityFilter([]);
    setConditionFilter([]);
    setNotOwnedFilter(false);
    setSortColumn(null);
    setSortType(null);
    setDeckSection('all');
  };

  const handleRefreshPrices = async () => {
    if (tableData.length === 0) return;

    const uniqueScryfallIds = [...new Set(tableData.map(e => e.scryfall_id))].filter(Boolean);
    if (uniqueScryfallIds.length === 0) return;

    setRefreshing(true);
    setRefreshProgress({ done: 0, total: uniqueScryfallIds.length });

    let updated = 0;
    let skipped = 0;

    for (const cardId of uniqueScryfallIds) {
      try {
        const res = await authFetch(`/cards/${cardId}/refresh`, { method: 'POST' });
        if (res.ok) {
          const refreshedCard = await res.json();
          setTableData(prevData => prevData.map(entry => {
            if (entry.scryfall_id !== cardId) return entry;

            const finish = entry.finish || 'nonfoil';
            let usdPrice;
            if (finish === 'foil') {
              usdPrice = refreshedCard?.prices?.usd_foil || refreshedCard?.prices?.usd || null;
            } else if (finish === 'etched') {
              usdPrice = refreshedCard?.prices?.usd_etched || refreshedCard?.prices?.usd || null;
            } else {
              usdPrice = refreshedCard?.prices?.usd || null;
            }
            const currentRaw = usdPrice ? Number(usdPrice) : null;
            const purchaseRaw = entry.purchase_price != null ? Number(entry.purchase_price) : null;
            const gainLossRaw = (purchaseRaw != null && currentRaw != null)
              ? currentRaw - purchaseRaw
              : null;

            return {
              ...entry,
              current_price: currentRaw != null ? `$${currentRaw.toFixed(2)}` : '',
              current_price_raw: currentRaw,
              gain_loss_raw: gainLossRaw,
              card_object: refreshedCard,
            };
          }));
          updated++;
        } else if (res.status === 429) {
          skipped++;
        }
      } catch (error) {
        console.error('Error refreshing card:', error);
      }
      setRefreshProgress(prev => ({ ...prev, done: prev.done + 1 }));
    }

    setRefreshing(false);
    const message = skipped > 0
      ? `${updated} price${updated !== 1 ? 's' : ''} updated, ${skipped} already current`
      : `${updated} price${updated !== 1 ? 's' : ''} refreshed`;
    toaster.push(
      <Message type="success" showIcon closable>{message}</Message>,
      { placement: 'topCenter', duration: 3000 }
    );
  };

  const handleCopyAcquisitionList = async () => {
    const notOwnedEntries = tableData
      .filter((e) => !e.is_sideboard && e.is_not_owned)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const text = notOwnedEntries.map((e) => `${e.quantity}x ${e.name}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toaster.push(
        <Message type="success" showIcon closable>
          Acquisition list copied to clipboard
        </Message>,
        { placement: 'topCenter', duration: TOAST_DURATION }
      );
    } catch {
      toaster.push(
        <Message type="error" showIcon closable>
          Could not copy to clipboard
        </Message>,
        { placement: 'topCenter', duration: TOAST_DURATION }
      );
    }
  };

  const handleBulkMarkOwnership = async () => {
    const is_not_owned = bulkMarkOwnershipModal === 'unowned';
    setBulkMarkLoading(true);
    try {
      const response = await authFetch(`/collections/${id}/entries/bulk-mark-ownership`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_not_owned, skip_proxies: skipProxies }),
      });
      if (response.ok) {
        const data = await response.json();
        toaster.push(
          <Message type="success" showIcon closable>
            {data.updated} entries marked as {is_not_owned ? 'unowned' : 'owned'}
          </Message>,
          { placement: 'topCenter', duration: TOAST_DURATION }
        );
        setBulkMarkOwnershipModal(null);
        setRefreshKey((k) => k + 1);
      } else {
        const data = await response.json();
        toaster.push(
          <Message type="error" showIcon closable>
            {data.error || 'Failed to update entries'}
          </Message>,
          { placement: 'topCenter', duration: TOAST_DURATION }
        );
      }
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          Unable to connect to server
        </Message>,
        { placement: 'topCenter', duration: TOAST_DURATION }
      );
    } finally {
      setBulkMarkLoading(false);
    }
  };

  const handleConvertProxies = async () => {
    setConvertProxiesLoading(true);
    try {
      const response = await authFetch(`/collections/${id}/entries/convert-proxies`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        toaster.push(
          <Message type="success" showIcon closable>
            {data.updated} proxy {data.updated === 1 ? 'entry' : 'entries'} converted to real copies
          </Message>,
          { placement: 'topCenter', duration: TOAST_DURATION }
        );
        setConvertProxiesModalOpen(false);
        setRefreshKey((k) => k + 1);
      } else {
        const data = await response.json();
        toaster.push(
          <Message type="error" showIcon closable>
            {data.error || 'Failed to convert proxies'}
          </Message>,
          { placement: 'topCenter', duration: TOAST_DURATION }
        );
      }
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          Unable to connect to server
        </Message>,
        { placement: 'topCenter', duration: TOAST_DURATION }
      );
    } finally {
      setConvertProxiesLoading(false);
    }
  };

  const handleRowClick = useCallback((rowData) => {
    setCardPopupEntry(rowData);
    setCardPopupOpen(true);
  }, []);

  const handleCardClick = useCallback((entry) => {
    setCardPopupEntry(entry);
    setCardPopupOpen(true);
  }, []);

  const handleEditClose = () => {
    setEditModalOpen(false);
    setEditEntry(null);
  };

  const handleEntryUpdated = () => {
    setRefreshKey((k) => k + 1);
  };

  const handleDeleteClose = () => {
    setDeleteModalOpen(false);
    setDeleteEntry(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteEntry) return;
    setDeleting(true);
    try {
      const response = await authFetch(
        `/collections/${id}/entries/${deleteEntry.id}`,
        { method: 'DELETE' }
      );
      if (response.ok || response.status === 204) {
        toaster.push(
          <Message type="success" showIcon closable>
            <strong>{deleteEntry.name}</strong> removed from {collection?.type === 'DECK' ? 'deck' : 'binder'}
          </Message>,
          { placement: 'topCenter', duration: 3000 }
        );
        handleDeleteClose();
        setRefreshKey((k) => k + 1);
      } else {
        const data = await response.json();
        toaster.push(
          <Message type="error" showIcon closable>
            {data.error || 'Failed to delete entry'}
          </Message>,
          { placement: 'topCenter', duration: 4000 }
        );
      }
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          Unable to connect to server
        </Message>,
        { placement: 'topCenter', duration: 4000 }
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.size === sortedData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedData.map((_, idx) => idx)));
    }
  };

  const handleToggleSelect = (rowIndex) => {
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(rowIndex)) {
      newSelectedIds.delete(rowIndex);
    } else {
      newSelectedIds.add(rowIndex);
    }
    setSelectedIds(newSelectedIds);
  };

  const handleBulkDeleteClose = () => {
    setBulkDeleteModalOpen(false);
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      // Map row indices back to actual entry IDs using tableGroupedData (which is what the table renders)
      const entryIds = Array.from(selectedIds).map((rowIndex) => tableGroupedData[rowIndex]?.id).filter(Boolean);
      const response = await authFetch(
        `/collections/${id}/entries/bulk-delete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entryIds }),
        }
      );
      if (response.ok || response.status === 204) {
        toaster.push(
          <Message type="success" showIcon closable>
            <strong>{selectedIds.size}</strong> entries removed from {collection?.type === 'DECK' ? 'deck' : 'binder'}
          </Message>,
          { placement: 'topCenter', duration: 3000 }
        );
        handleBulkDeleteClose();
        setSelectedIds(new Set());
        setRefreshKey((k) => k + 1);
      } else {
        const data = await response.json();
        toaster.push(
          <Message type="error" showIcon closable>
            {data.error || 'Failed to delete entries'}
          </Message>,
          { placement: 'topCenter', duration: 4000 }
        );
      }
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          Unable to connect to server
        </Message>,
        { placement: 'topCenter', duration: 4000 }
      );
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleAddModalClose = () => {
    setAddModalOpen(false);
    if (addedViaDropRef.current) {
      addedViaDropRef.current = false;
      return;
    }
  };

  const handleAddModalSuccess = () => {
    setAddModalOpen(false);
    setRefreshKey((k) => k + 1);
    if (addedViaDropRef.current) {
      addedViaDropRef.current = false;
      return;
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverActive(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverActive(false);

    const parsed = parseDropData(e.dataTransfer);
    if (!parsed) {
      toaster.push(
        <Message showIcon type="error" closable>
          Could not identify the dropped card. Try dragging a card image from EDHREC, TCGPlayer, or CardKingdom.
        </Message>,
        { placement: 'topCenter', duration: TOAST_DURATION }
      );
      return;
    }

    setResolvingDrop(true);
    try {
      const card = await resolveCardFromDrop(e.dataTransfer);
      if (card) {
        addedViaDropRef.current = true;
        setAddCardData({
          scryfallId: card.id,
          cardName: card.name,
          card: card,
        });
        setAddModalOpen(true);
      } else {
        toaster.push(
          <Message showIcon type="error" closable>
            Could not find that card in Scryfall. Try dragging a different card.
          </Message>,
          { placement: 'topCenter', duration: TOAST_DURATION }
        );
      }
    } catch (error) {
      console.error('Error resolving dropped card:', error);
      toaster.push(
        <Message showIcon type="error" closable>
          Error loading card details. Please try again.
        </Message>,
        { placement: 'topCenter', duration: TOAST_DURATION }
      );
    } finally {
      setResolvingDrop(false);
    }
  };

  const handleCardDropFromVisual = async (e) => {
    // Reuse the same drop handling logic
    await handleDrop(e);
  };

  const checkboxColumn = {
    key: 'checkbox',
    label: '',
    width: 50,
    fixed: true,
    custom: 'checkbox',
  };

  const actionsColumn = { key: 'actions', label: '', width: 120, custom: 'actions' };

  const columns = [
    ...(canEdit ? [checkboxColumn] : []),
    ...allColumns,
    ...(canEdit ? [actionsColumn] : []),
  ];

  const renderStatsSection = () => {
    if (!stats || tableData.length === 0) return null;
    // Suppress all stats panels for Wish List — mana curve, color distribution,
    // card-type breakdown, and format validation are not meaningful for wanted cards.
    if (collection?.type === 'WISHLIST') return null;

    // Color breakdown pie chart (simple bars)
    const colorEntries = Object.entries(stats.colorBreakdown).sort((a, b) => b[1] - a[1]);
    const maxColorCount = Math.max(...colorEntries.map(e => e[1]), 1);

    // Rarity breakdown
    const rarityOrder = { 'Common': 0, 'Uncommon': 1, 'Rare': 2, 'Mythic': 3 };
    const rarityEntries = Object.entries(stats.rarityBreakdown)
      .sort((a, b) => (rarityOrder[a[0]] ?? 99) - (rarityOrder[b[0]] ?? 99));
    const maxRarityCount = Math.max(...rarityEntries.map(e => e[1]), 1);

    return (
      <Panel
        style={{
          marginBottom: 20,
          backgroundColor: '#2c2c2c',
          borderColor: '#444',
        }}
        bordered
        header={
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => updateDeckPref('statsExpanded', !deckPrefs.statsExpanded)}
          >
            <h4 style={{ margin: 0 }}>{
              collection?.type === 'DECK' ? 'Deck Statistics'
              : collection?.type === 'TRADE_BINDER' ? 'Trade Binder'
              : 'Collection Statistics'
            }</h4>
            <div style={{ padding: '4px 8px', transform: deckPrefs.statsExpanded ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }}>
              <ArrowDown />
            </div>
          </div>
        }
      >
        {deckPrefs.statsExpanded && (
        <>
        {collection?.type === 'DECK' && (
          <DeckInsights tableData={tableData} />
        )}
        {hasSideboard && sideboardCards > 0 && (
          <div style={{ fontSize: 13, marginBottom: 12, padding: '8px 12px', backgroundColor: '#333', borderRadius: 4 }}>
            Main Deck: <strong>{mainDeckCards}</strong> cards | Sideboard: <strong>{sideboardCards}</strong> cards
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 24, padding: '12px 0' }}>
          {/* Color Breakdown — shown for non-deck collections only; decks use DeckInsights */}
          {collection?.type !== 'DECK' && (
          <div>
            <h5 style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>Colors</h5>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {colorEntries.map(([color, count]) => {
                const barWidth = (count / maxColorCount) * 100;
                return (
                  <div key={color} style={{ fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span>{color}</span>
                      <span>{count}</span>
                    </div>
                    <div
                      style={{
                        width: '100%',
                        height: 6,
                        backgroundColor: '#444',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${barWidth}%`,
                          height: '100%',
                          backgroundColor: '#3498db',
                          transition: 'width 0.2s',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {/* Rarity Breakdown */}
          <div>
            <h5 style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>Rarity</h5>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rarityEntries.map(([rarity, count]) => {
                const barWidth = (count / maxRarityCount) * 100;
                return (
                  <div key={rarity} style={{ fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span>{rarity}</span>
                      <span>{count}</span>
                    </div>
                    <div
                      style={{
                        width: '100%',
                        height: 6,
                        backgroundColor: '#444',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${barWidth}%`,
                          height: '100%',
                          backgroundColor: '#2ecc71',
                          transition: 'width 0.2s',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Summary Stats */}
          <div>
            <h5 style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>Summary</h5>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Total Cards:</span>
                <strong>{stats.totalCardCount}</strong>
              </div>
              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Total Purchase Value:</span>
                  <strong>${stats.totalPurchaseValue.toFixed(2)}</strong>
                </div>
              )}
              {deckCompletion !== null && (
                deckCompletion.total - deckCompletion.owned === 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Acquisition:</span>
                    <strong style={{ color: COLORS.gain }}>Deck Complete!</strong>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Owned:</span>
                      <strong>{deckCompletion.owned} / {deckCompletion.total}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Cards to Acquire:</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <strong>{deckCompletion.total - deckCompletion.owned}</strong>
                        {tableData.some(e => !e.is_sideboard && e.is_not_owned) && (
                          <Button size="xs" appearance="ghost" onClick={handleCopyAcquisitionList}>
                            Copy List
                          </Button>
                        )}
                      </div>
                    </div>
                  </>
                )
              )}
            </div>
          </div>

          {/* Top 10 Most Valuable */}
          {canEdit && stats.top10MostValuableCards.length > 0 && (
            <div style={{ gridColumn: isMobile ? 'auto' : '1 / -1' }}>
              <h5 style={{ marginTop: 0, marginBottom: 12, fontSize: 14 }}>Top 10 Most Valuable</h5>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 250, overflowY: 'auto' }}>
                {stats.top10MostValuableCards.map((card) => (
                <div
                  key={card.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 12,
                      paddingBottom: 4,
                      borderBottom: '1px solid #444',
                    }}
                  >
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {card.name}
                      {card.quantity > 1 && <span style={{ color: COLORS.muted }}> x{card.quantity}</span>}
                    </span>
                    <span style={{ marginLeft: 8, whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                      ${card.totalValue.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </Panel>
    );
  };

  const validateDeck = async () => {
    setValidationLoading(true);
    setValidationOpen(true);
    
    try {
      const response = await authFetch(`/collections/${id}/validate`);
      if (!isMountedRef.current) return;
      
      if (!response.ok) {
        toaster.push(
          <Message type="error" showIcon closable>
            Failed to validate deck
          </Message>,
          { placement: 'topCenter' }
        );
        return;
      }
      
      const result = await response.json();
      if (isMountedRef.current) {
        setValidationResult(result);
      }
    } catch (err) {
      console.error('Validation error:', err);
      toaster.push(
        <Message type="error" showIcon closable>
          Error validating deck
        </Message>,
        { placement: 'topCenter' }
      );
    } finally {
      if (isMountedRef.current) {
        setValidationLoading(false);
      }
    }
  };

  // Clear validation when table data changes
  useEffect(() => {
    setValidationResult(null);
  }, [refreshKey]);

  // Clear selections when table data changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [refreshKey]);

  if (loading) {
    return (
      <>
        <NavigationBar />
        <Container>
          <Content>
            <Loader center size="md" content="Loading collection..." style={{ marginTop: 100 }} />
          </Content>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <NavigationBar />
        <Container>
          <Content>
            <div style={{ maxWidth: SPACING.containerMaxWidth, margin: `${SPACING.containerMarginTop}px auto`, padding: `0 ${SPACING.containerPadding}px` }}>
              <Message type="error" showIcon>{error}</Message>
            </div>
          </Content>
        </Container>
      </>
    );
  }

  return (
    <>
      <NavigationBar />
      <Container>
        <Content>
          <div style={{ maxWidth: SPACING.containerMaxWidth, margin: `${SPACING.containerMarginTop}px auto`, padding: `0 ${SPACING.containerPadding}px` }}>
            <div style={{ marginBottom: SPACING.headerMarginBottom }}>
              <ErrorBoundary>
                <DeckHeader
                  collection={collection}
                  canEdit={canEdit}
                  tableData={tableData}
                  validationLoading={validationLoading}
                  onEditClick={() => setEditDeckModalOpen(true)}
                  onAddCardClick={() => { setAddSidePanelModalOpen(true); }}
                  onValidateClick={validateDeck}
                  onImportClick={() => setImportDeckModalOpen(true)}
                  onMarkOwnedClick={() => { setSkipProxies(true); setBulkMarkOwnershipModal('owned'); }}
                  onMarkUnownedClick={() => { setSkipProxies(true); setBulkMarkOwnershipModal('unowned'); }}
                  onConvertProxiesClick={() => setConvertProxiesModalOpen(true)}
                  onExportClick={() => setExportOpen(true)}
                  onShareClick={() => setShareModalOpen(true)}
                  onMoreMenuClick={() => setMoreMenuOpen(true)}
                  onPlaytestClick={() => navigate(`/library/${id}/playtest`)}
                  COLORS={COLORS}
                />
              </ErrorBoundary>
              <ErrorBoundary>
                <DeckSummary
                  tableData={tableData}
                  priceLoading={priceLoading}
                  cardLoading={cardLoading}
                  priceSummary={priceSummary}
                  totalPages={totalPages}
                  currentPageNum={currentPageNum}
                  paginationProgress={paginationProgress}
                  canEdit={canEdit}
                  COLORS={COLORS}
                  onRefreshPrices={handleRefreshPrices}
                  refreshing={refreshing}
                  refreshProgress={refreshProgress}
                />
              </ErrorBoundary>
            </div>

            {tableData.length > 0 && (
              <CollectionFilters
                nameSearch={nameSearch}
                setNameSearch={setNameSearch}
                colorFilter={colorFilter}
                setColorFilter={setColorFilter}
                rarityFilter={rarityFilter}
                setRarityFilter={setRarityFilter}
                conditionFilter={conditionFilter}
                setConditionFilter={setConditionFilter}
                onClearFilters={handleClearFilters}
                totalCount={tableData.length}
                filteredCount={sortedData.length}
                notOwnedFilter={collection?.type === 'DECK' ? notOwnedFilter : undefined}
                setNotOwnedFilter={collection?.type === 'DECK' ? setNotOwnedFilter : undefined}
              />
            )}

            {hasSideboard && (
              <div style={{ marginBottom: 16 }}>
                <ButtonGroup>
                  <Button
                    appearance={deckSection === 'all' ? 'primary' : 'default'}
                    size="sm"
                    onClick={() => setDeckSection('all')}
                  >
                    All
                  </Button>
                  <Button
                    appearance={deckSection === 'main' ? 'primary' : 'default'}
                    size="sm"
                    onClick={() => setDeckSection('main')}
                  >
                    Main Deck ({mainDeckCards})
                  </Button>
                  <Button
                    appearance={deckSection === 'side' ? 'primary' : 'default'}
                    size="sm"
                    onClick={() => setDeckSection('side')}
                  >
                    Sideboard ({sideboardCards})
                  </Button>
                </ButtonGroup>
              </div>
            )}

            {renderStatsSection()}

            <DeckControlsBar
              tableData={tableData}
              viewMode={viewMode}
              onViewModeChange={(mode) => {
                setViewMode(mode);
                updatePreference('card_display_mode', mode);
                if (mode === VIEW_MODES.VISUAL) {
                  setSortColumn(null);
                  setSortType(null);
                }
              }}
              cardLoading={cardLoading}
              deckPrefs={deckPrefs}
              onGroupByChange={(val) => updateDeckPref('sortBy', val)}
              sortColumn={sortColumn}
              allColumns={allColumns}
              selectedCount={selectedIds.size}
              onBulkDelete={() => setBulkDeleteModalOpen(true)}
              collection={collection}
              canEdit={canEdit}
              spacing={SPACING}
              colors={COLORS}
            />

            <ErrorBoundary>
              <DeckContent
              viewMode={viewMode}
              cardLoading={cardLoading}
              tableData={tableData}
              sortedData={sortedData}
              groupedData={groupedData}
              tableGroupedData={tableGroupedData}
              sortColumn={sortColumn}
              sortType={sortType}
              displaySortColumn={displaySortColumn}
              columns={columns}
              navigate={navigate}
              onRowClick={handleRowClick}
              onSortColumn={handleSortColumn}
              onCardClick={handleCardClick}
              onCardDrop={handleCardDropFromVisual}
              validationHighlights={validationHighlights}
              collection={collection}
              preferences={preferences}
              dragOverActive={dragOverActive}
              resolvingDrop={resolvingDrop}
              handleDragOver={handleDragOver}
              handleDragEnter={handleDragEnter}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              COLORS={COLORS}
              SPACING={SPACING}
              selectedIds={selectedIds}
              onSelectAll={handleSelectAll}
              onToggleSelect={handleToggleSelect}
              />
            </ErrorBoundary>

               <CardQuickViewModal
                 open={cardPopupOpen}
                 entry={cardPopupEntry}
                 collectionId={id}
                 collection={collection}
                 canEdit={canEdit}
                 onClose={() => setCardPopupOpen(false)}
                 onUpdated={() => setRefreshKey((k) => k + 1)}
                 onRemoved={() => setRefreshKey((k) => k + 1)}
               />

               {canEdit && (
                <>
                <EditEntryModal
              open={editModalOpen}
              onClose={handleEditClose}
              entry={editEntry}
              collectionId={id}
              deckType={collection?.deck_type}
              collectionType={collection?.type}
              onUpdated={handleEntryUpdated}
            />

            <DeleteEntryModal
              open={deleteModalOpen}
              onClose={handleDeleteClose}
              cardName={deleteEntry?.name}
              collectionName={collection?.name}
              collectionType={collection?.type}
              onConfirm={handleDeleteConfirm}
              loading={deleting}
            />

            <BulkDeleteModal
              open={bulkDeleteModalOpen}
              onClose={handleBulkDeleteClose}
              selectedCount={selectedIds.size}
              collectionName={collection?.name}
              onConfirm={handleBulkDeleteConfirm}
              loading={bulkDeleting}
            />

            <MarkOwnershipModal
              open={bulkMarkOwnershipModal !== null}
              onClose={() => setBulkMarkOwnershipModal(null)}
              mode={bulkMarkOwnershipModal}
              skipProxies={skipProxies}
              onSkipProxiesChange={setSkipProxies}
              onConfirm={handleBulkMarkOwnership}
              loading={bulkMarkLoading}
            />

            <ConvertProxiesModal
              open={convertProxiesModalOpen}
              onClose={() => setConvertProxiesModalOpen(false)}
              onConfirm={handleConvertProxies}
              loading={convertProxiesLoading}
            />

            <ExportModal
              open={exportOpen}
              onClose={() => setExportOpen(false)}
              collectionName={collection?.name}
              entries={tableData}
              collectionType={collection?.type}
            />

            <ShareCollectionModal
              open={shareModalOpen}
              onClose={() => setShareModalOpen(false)}
              collection={collection}
            />

            <EditDeckModal
              open={editDeckModalOpen}
              onClose={() => setEditDeckModalOpen(false)}
              collection={collection}
              onUpdated={() => {
                setRefreshKey((prev) => prev + 1);
              }}
            />

            <AddToCollectionModal
              open={addModalOpen}
              onClose={handleAddModalClose}
              onSuccess={handleAddModalSuccess}
              scryfallId={addCardData?.scryfallId}
              cardName={addCardData?.cardName}
              card={addCardData?.card}
              preSelectedCollectionId={parseInt(id, 10)}
            />

            <AddCardSidePanelModal
              open={addSidePanelModalOpen}
              onClose={() => setAddSidePanelModalOpen(false)}
              onSuccess={() => setRefreshKey((k) => k + 1)}
              preSelectedCollectionId={parseInt(id, 10)}
            />

            <ImportDeckModal
              open={importDeckModalOpen}
              onClose={() => setImportDeckModalOpen(false)}
              onSuccess={() => setRefreshKey((k) => k + 1)}
              preSelectedCollectionId={parseInt(id, 10)}
            />

            <Modal open={moreMenuOpen} onClose={() => setMoreMenuOpen(false)} size="xs">
              <Modal.Header>
                <Modal.Title>More Actions</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Button
                    block
                    size="sm"
                    appearance="ghost"
                    onClick={() => {
                      setImportDeckModalOpen(true);
                      setMoreMenuOpen(false);
                    }}
                  >
                    Import Cards
                  </Button>
                  {collection.type === 'DECK' && tableData.length > 0 && (
                    <>
                      <Button
                        block
                        size="sm"
                        appearance="ghost"
                        onClick={() => {
                          setSkipProxies(true);
                          setBulkMarkOwnershipModal('owned');
                          setMoreMenuOpen(false);
                        }}
                      >
                        Mark All Owned
                      </Button>
                      <Button
                        block
                        size="sm"
                        appearance="ghost"
                        onClick={() => {
                          setSkipProxies(true);
                          setBulkMarkOwnershipModal('unowned');
                          setMoreMenuOpen(false);
                        }}
                      >
                        Mark All Unowned
                      </Button>
                      {tableData.some((e) => e.is_proxy) && (
                        <Button
                          block
                          size="sm"
                          appearance="ghost"
                          onClick={() => {
                            setConvertProxiesModalOpen(true);
                            setMoreMenuOpen(false);
                          }}
                        >
                          Convert Proxies
                        </Button>
                      )}
                    </>
                  )}
                  {tableData.length > 0 && (
                    <Button
                      block
                      size="sm"
                      appearance="ghost"
                      onClick={() => {
                        setExportOpen(true);
                        setMoreMenuOpen(false);
                      }}
                    >
                      Export
                    </Button>
                  )}
                  <Button
                    block
                    size="sm"
                    appearance="ghost"
                    onClick={() => {
                      setShareModalOpen(true);
                      setMoreMenuOpen(false);
                    }}
                  >
                    Share
                  </Button>
                </div>
              </Modal.Body>
            </Modal>

            <Modal open={validationOpen} onClose={() => setValidationOpen(false)} size="lg">
              <Modal.Header>
                <Modal.Title>
                  {collection?.deck_type} Deck Validation
                </Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ maxHeight: '70vh', overflow: 'auto' }}>
                {validationLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                    <Loader center size="md" content="Validating deck..." />
                  </div>
                ) : validationResult ? (
                  <>
                    {collection?.deck_type === 'PLANAR_STANDARD' && formatRules && (
                      <Panel shaded style={{ marginBottom: 16, borderLeft: '3px solid #3498db' }}>
                        <h6 style={{ marginBottom: 8 }}>Format Info</h6>
                        {formatRules.description && (
                          <p style={{ fontSize: 13, color: '#aaa', marginBottom: 8 }}>{formatRules.description}</p>
                        )}
                        <div style={{ marginBottom: 6, fontSize: 13 }}>
                          <strong>Legal Sets:</strong>{' '}
                          {formatRules.legalSetNames
                            ? formatRules.legalSets.map(code => formatRules.legalSetNames[code] || code.toUpperCase()).join(', ')
                            : formatRules.legalSets.map(s => s.toUpperCase()).join(', ')}
                        </div>
                        <div style={{ fontSize: 13 }}>
                          <strong>Banned Cards:</strong>{' '}
                          {formatRules.bannedCards && formatRules.bannedCards.length > 0
                            ? formatRules.bannedCards.join(', ')
                            : 'None'}
                        </div>
                        {formatRules.rotationNote && (
                          <p style={{ fontSize: 12, color: '#888', marginTop: 8, fontStyle: 'italic' }}>{formatRules.rotationNote}</p>
                        )}
                      </Panel>
                    )}

                    {validationResult.valid ? (
                      <Message
                        type="success"
                        showIcon
                        style={{ marginBottom: 16 }}
                      >
                        Deck is legal for {collection?.deck_type}!
                      </Message>
                    ) : null}

                    {validationResult.errors && validationResult.errors.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <h5 style={{ marginBottom: 8, color: '#e74c3c' }}>Errors</h5>
                        {validationResult.errors.map((error, idx) => (
                          <Panel key={idx} style={{ marginBottom: 8 }} shaded>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                              <span style={{ color: error.type === 'BANNED_CARD' ? '#9b59b6' : '#e74c3c', fontSize: 18, flexShrink: 0 }}>
                                {error.type === 'BANNED_CARD' ? '⊘' : '✕'}
                              </span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                                  {error.type === 'BANNED_CARD' ? 'BANNED CARD' : error.type === 'SET_LEGALITY' ? 'SET LEGALITY' : error.type}
                                </div>
                                <div style={{ marginBottom: 8, fontSize: 13 }}>
                                  {error.message}
                                </div>
                                {error.cards && error.cards.length > 0 && (
                                  <div style={{ fontSize: 12 }}>
                                    Cards: {error.cards.map((card, cardIdx) => (
                                      <span key={cardIdx}>
                                        <a
                                          href={`/cardsearch/${card.scryfall_id}`}
                                          style={{ color: '#3498db', textDecoration: 'none' }}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            navigate(`/cardsearch/${card.scryfall_id}`);
                                          }}
                                        >
                                          {card.name}
                                        </a>
                                        {cardIdx < error.cards.length - 1 ? ', ' : ''}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </Panel>
                        ))}
                      </div>
                    )}

                    {validationResult.warnings && validationResult.warnings.length > 0 && (
                      <div>
                        <h5 style={{ marginBottom: 8, color: '#f39c12' }}>Warnings</h5>
                        {validationResult.warnings.map((warning, idx) => (
                          <Panel key={idx} style={{ marginBottom: 8 }} shaded>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                              <span style={{ color: '#f39c12', fontSize: 18, flexShrink: 0 }}>⚠</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                                  {warning.type}
                                </div>
                                <div style={{ marginBottom: 8, fontSize: 13 }}>
                                  {warning.message}
                                </div>
                                {warning.cards && warning.cards.length > 0 && (
                                  <div style={{ fontSize: 12 }}>
                                    Cards: {warning.cards.map((card, cardIdx) => (
                                      <span key={cardIdx}>
                                        <a
                                          href={`/cardsearch/${card.scryfall_id}`}
                                          style={{ color: '#3498db', textDecoration: 'none' }}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            navigate(`/cardsearch/${card.scryfall_id}`);
                                          }}
                                        >
                                          {card.name}
                                        </a>
                                        {cardIdx < warning.cards.length - 1 ? ', ' : ''}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </Panel>
                        ))}
                      </div>
                    )}

                    {!validationResult.valid && (!validationResult.errors || validationResult.errors.length === 0) && (
                      <Message type="error" showIcon>
                        Deck validation failed
                      </Message>
                    )}
                  </>
                ) : null}
              </Modal.Body>
            </Modal>
                </>
              )}
          </div>
        </Content>
      </Container>
    </>
  );
};

export default CollectionDetail;
